import { useTranslation } from 'next-i18next'

import { CSSProperties, RefObject, useEffect, useState } from 'react'
import { useDebouncedCallback } from 'use-debounce'

import Input from '~/bootcamp/Activities/components/Input'

function isPrime(num: number): boolean {
  if (num <= 1) return false
  if (num === 2) return true

  // storing the calculated value would be much
  // better than calculating in each iteration
  const sqrt = Math.sqrt(num)

  // eslint-disable-next-line fp/no-loops
  for (let i = 2; i <= sqrt; i++) {
    if (num % i === 0) return false
  }
  return true
}

interface Props {
  className?: string
  style?: CSSProperties
  spaceBetween?: number | string
  compound: number | null
  branchIndices: number
  showBranch: boolean
  isCompleted: boolean
  onNewNode: (nodeIndices: number, nodeCompoundNumber: number, direction: 'left' | 'right') => void
  arrowLeftStyle?: CSSProperties
  arrowRightStyle?: CSSProperties
  leftInputRef?: RefObject<HTMLInputElement>
  rightInputRef?: RefObject<HTMLInputElement>
}

const FactorNode = (props: Props): React.ReactElement => {
  const { t } = useTranslation('generics')

  const {
    className = '',
    style,
    spaceBetween = 10,
    compound,
    branchIndices,
    showBranch,
    isCompleted,
    onNewNode,
    arrowLeftStyle,
    arrowRightStyle,
    leftInputRef,
    rightInputRef,
  } = props

  const [branchValue, setBranchValue] = useState({ leftBranch: '', rightBranch: '' })

  const openBranch = (): void => {
    const leftValue = parseInt(branchValue.leftBranch)
    const rightValue = parseInt(branchValue.rightBranch)

    if (leftValue * rightValue !== compound) return
    if (leftValue < 2 || rightValue < 2) return

    const isLeftValueCompound = !isPrime(leftValue)
    const isRightValueCompound = !isPrime(rightValue)

    if (isLeftValueCompound) {
      onNewNode(branchIndices, leftValue, 'left')
    }

    if (isRightValueCompound) {
      onNewNode(branchIndices, rightValue, 'right')
    }
  }

  const debounced = useDebouncedCallback(
    () => {
      openBranch()
    },
    1000,
    { trailing: true },
  )

  useEffect(() => {
    debounced()
  }, [branchValue, debounced])

  const inputChangeHandler = (value: string, name: string): void => {
    setBranchValue((exValue) => ({ ...exValue, [name]: value }))
  }

  const leftBranch = (
    <div className="flex flex-col items-center justify-center w-fit">
      <BranchLine style={arrowLeftStyle} />
      <Input
        aria-label={t('ariaLabelButtons.insertPrimeFactor')}
        disabled={isCompleted}
        maxDigits={3}
        name="leftBranch"
        onChange={inputChangeHandler}
        ref={leftInputRef}
        value={branchValue.leftBranch}
      />
    </div>
  )

  const rightBranch = (
    <div className="flex flex-col items-center justify-center w-fit">
      <BranchLine arrow="right" style={arrowRightStyle} />
      <Input
        aria-label={t('ariaLabelButtons.insertPrimeFactor')}
        disabled={isCompleted}
        maxDigits={3}
        name="rightBranch"
        onChange={inputChangeHandler}
        ref={rightInputRef}
        value={branchValue.rightBranch}
      />
    </div>
  )

  const spacer = <div style={{ width: spaceBetween }} />

  const visibilityClass = showBranch ? 'visible' : 'invisible'

  return (
    <div className={`flex ${visibilityClass} ${className}`} style={style}>
      {leftBranch}
      {spacer}
      {rightBranch}
    </div>
  )
}

const BranchLine = ({
  arrow = 'left',
  style,
  className = '',
}: {
  arrow?: 'left' | 'right'
  style?: CSSProperties
  className?: string
}): React.ReactElement => {
  const arrowClassNames = {
    left: 'ml-3 rotate-20',
    right: 'mr-3 -rotate-20',
  }

  return (
    <div
      className={`w-1 h-9 bg-purple rounded mb-2 transform rotate ${arrowClassNames[arrow]} ${className}`}
      style={style}
    />
  )
}

export default FactorNode
